import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  faIdCard,
  faBriefcase,
  faBuildingColumns,
  faCreditCard,
  faCheck,
  faEnvelope,
  faMobileScreen,
} from '@fortawesome/pro-duotone-svg-icons'

import Button from '../button/Button'
import Icon from '../icon/Icon'
import CreditCheckModal from '../credit-check-modal/CreditCheckModal'
import ToggleSwitch from '../toggle-switch/ToggleSwitch'
import PlaidConnectButton from './PlaidConnectButton'
import ContactVerificationRow from '../contact-verification-row/ContactVerificationRow'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import {
  setVerificationVisibility,
  startIdentityVerification,
  syncCreditCheck,
  syncIdentityVerification,
} from '../../_data/verifications'
import {
  QuickPassVerification,
  QuickPassVerificationType,
} from '../../_types/quickpass-verification'
import { QUICKCASA_PRIVACY_POLICY_URL } from '../../_configuration/quickpass-configuration'
import { IDENTITY_RETURN_PARAMETER, resolveStatusDisplay } from './helpers'
import { BannerTone, VerificationsManagerState } from './types'
import {
  BankActions,
  Banner,
  GroupTitle,
  Lede,
  ManagerRoot,
  PrivacyNote,
  PrivacyPolicyLink,
  RowIcon,
  RowLeft,
  RowRight,
  RowSubtitle,
  RowText,
  RowTitle,
  StatusPill,
  VerificationRow,
  VisibilityDescription,
  VisibilityName,
  VisibilityRow,
  VisibilityText,
} from './styled'

const initialiseState = (): VerificationsManagerState => ({
  isBusy: false,
  isCreditModalOpen: false,
  isSyncingCredit: false,
  isUpdatingCreditVisibility: false,
  bannerMessage: '',
  bannerTone: 'info',
})

/**
 * "Verifications" section on the profile page. Renders the renter's identity
 * verification status and runs the Didit flow: it asks the backend for a
 * session and sends the renter to Didit's hosted page, which returns them here
 * with a marker in the query string that triggers a sync.
 *
 * Didit hosts its flow off site rather than embedding, so the renter leaves the
 * app entirely and the component cannot watch the flow finish. The return
 * marker is therefore the only signal that an attempt just happened, and the
 * sync it triggers is what stops a renter landing back on a row that still says
 * unverified. We never see or store the ID document, only the verified result
 * lands on the profile.
 *
 * @returns {JSX.Element | null} null while auth is resolving
 */
const VerificationsManager = (): JSX.Element | null => {
  const { quickPassUser } = useAuth()
  const { profile, refreshProfile } = useQuickPassProfile()
  const [state, setState] = useState<VerificationsManagerState>(initialiseState)
  const [searchParams, setSearchParams] = useSearchParams()

  const hasReturnedFromDidit =
    searchParams.get(IDENTITY_RETURN_PARAMETER) !== null
  const quickPassUserId = quickPassUser?.id ?? ''

  useEffect(() => {
    if (hasReturnedFromDidit === false || quickPassUserId === '') {
      return
    }

    const syncOnReturn = async (): Promise<void> => {
      await syncIdentityVerification(quickPassUserId)
      await refreshProfile()
    }

    // Cleared straight away so a refresh, or a renter sharing the URL, cannot
    // replay the return and re-announce an outcome they have already seen.
    setSearchParams(
      previous => {
        previous.delete(IDENTITY_RETURN_PARAMETER)
        return previous
      },
      { replace: true },
    )

    void syncOnReturn()
  }, [hasReturnedFromDidit, quickPassUserId, refreshProfile, setSearchParams])

  if (quickPassUser === null) {
    return null
  }

  const findVerification = (
    type: QuickPassVerificationType,
  ): QuickPassVerification | null => {
    if (profile === null || Array.isArray(profile.verifications) === false) {
      return null
    }
    return profile.verifications.find(entry => entry.type === type) ?? null
  }

  const emailVerification = findVerification('email')
  const phoneVerification = findVerification('phone')
  const identity = findVerification('identity')
  const income = findVerification('income')
  const funds = findVerification('funds')
  const credit = findVerification('credit')

  const creditStatus = credit?.status ?? 'unverified'
  const creditDisplay = resolveStatusDisplay(creditStatus)
  const isCreditInFlight =
    creditStatus === 'processing' || creditStatus === 'pending'
  const isCreditPublic = credit?.isPublic === true
  // A completed check with nothing affirmative to report is an adverse result.
  // The share page can only say "credit checked, creditworthiness confirmed", so
  // there is nothing honest for the renter to publish and no switch to offer
  // them. The server refuses the same case; this only keeps the UI from dangling
  // a control that would be turned down.
  const canShowCreditPublicly =
    creditStatus === 'verified' && credit !== null && credit.displayValue !== ''

  let creditSubtitle =
    'Show landlords a verified credit result instead of a number you typed in.'

  if (credit !== null && credit.displayValue !== '') {
    creditSubtitle = credit.displayValue
  } else if (creditStatus === 'verified') {
    creditSubtitle =
      'Your check came back. There is no clear-credit result to show, so this one stays private to you.'
  }

  const identityStatus = identity?.status ?? 'unverified'
  const identityDisplay = resolveStatusDisplay(identityStatus)
  const isIdentityVerified = identityStatus === 'verified'

  const incomeDisplay = resolveStatusDisplay(income?.status ?? 'unverified')
  const fundsDisplay = resolveStatusDisplay(funds?.status ?? 'unverified')
  const isBankConnected = income !== null || funds !== null

  const handleStatusMessage = (message: string, tone: BannerTone): void => {
    setState(previous => ({
      ...previous,
      bannerMessage: message,
      bannerTone: tone,
    }))
  }

  const handleToggleCreditVisibility = async (): Promise<void> => {
    const next = isCreditPublic === false

    setState(previous => ({ ...previous, isUpdatingCreditVisibility: true }))

    const result = await setVerificationVisibility(
      quickPassUser.id,
      'credit',
      next,
    )
    await refreshProfile()

    setState(previous => ({
      ...previous,
      isUpdatingCreditVisibility: false,
      bannerMessage:
        result === null
          ? 'We could not change that just now. Please try again shortly.'
          : '',
      bannerTone: result === null ? 'error' : 'info',
    }))
  }

  const handleSyncCredit = async (): Promise<void> => {
    setState(previous => ({ ...previous, isSyncingCredit: true }))

    const result = await syncCreditCheck(quickPassUser.id)
    await refreshProfile()

    setState(previous => ({
      ...previous,
      isSyncingCredit: false,
      bannerMessage:
        result === null
          ? 'We could not refresh your credit check just yet. Please try again shortly.'
          : '',
      bannerTone: result === null ? 'error' : 'info',
    }))
  }

  const handleVerify = async (): Promise<void> => {
    setState(previous => ({
      ...previous,
      isBusy: true,
      bannerMessage: '',
      bannerTone: 'info',
    }))

    const session = await startIdentityVerification(quickPassUser.id)
    if (session === null || session.verificationUrl === '') {
      setState(previous => ({
        ...previous,
        isBusy: false,
        bannerMessage: 'Could not start verification. Please try again.',
        bannerTone: 'error',
      }))
      return
    }

    // Leaves QuickPass entirely. `isBusy` is deliberately left set: the renter
    // should see the button held while the browser navigates, and this component
    // is about to be torn down anyway.
    window.location.assign(session.verificationUrl)
  }

  return (
    <ManagerRoot>
      <Lede>
        Verified badges are a strong trust signal for landlords. Complete them
        once and they travel with your QuickPass.
      </Lede>

      <GroupTitle>Contact details</GroupTitle>

      <ContactVerificationRow
        quickPassUserId={quickPassUserId}
        channel="email"
        title="Email address"
        subtitle="Prove you can be reached at the address landlords will write to."
        icon={faEnvelope}
        verification={emailVerification}
        defaultDestination={quickPassUser.email}
        onVerified={() => {
          void refreshProfile()
        }}
      />

      <ContactVerificationRow
        quickPassUserId={quickPassUserId}
        channel="phone"
        title="Mobile number"
        subtitle="Prove your number so a landlord can call or text you directly."
        icon={faMobileScreen}
        verification={phoneVerification}
        defaultDestination={quickPassUser.phone ?? ''}
        onVerified={() => {
          void refreshProfile()
        }}
      />

      <GroupTitle>Identity</GroupTitle>

      <VerificationRow>
        <RowLeft>
          <RowIcon>
            <Icon icon={faIdCard} label="Identity" />
          </RowIcon>
          <RowText>
            <RowTitle>Government ID</RowTitle>
            <RowSubtitle>
              Confirm your identity with a photo of your ID and a selfie.
            </RowSubtitle>
          </RowText>
        </RowLeft>
        <RowRight>
          <StatusPill tone={identityDisplay.tone}>
            {isIdentityVerified ? <Icon icon={faCheck} /> : null}
            {identityDisplay.label}
          </StatusPill>
          <Button
            type="button"
            variant={isIdentityVerified ? 'secondary' : 'primary'}
            size="small"
            isLoading={state.isBusy}
            onClick={() => {
              void handleVerify()
            }}
          >
            {isIdentityVerified ? 'Re-verify' : 'Verify ID'}
          </Button>
        </RowRight>
      </VerificationRow>

      <GroupTitle>Bank-verified</GroupTitle>

      <VerificationRow>
        <RowLeft>
          <RowIcon>
            <Icon icon={faBriefcase} label="Income" />
          </RowIcon>
          <RowText>
            <RowTitle>Income</RowTitle>
            <RowSubtitle>
              {income !== null && income.displayValue !== ''
                ? income.displayValue
                : 'Verify your income straight from your bank.'}
            </RowSubtitle>
          </RowText>
        </RowLeft>
        <RowRight>
          <StatusPill tone={incomeDisplay.tone}>
            {income?.status === 'verified' ? <Icon icon={faCheck} /> : null}
            {incomeDisplay.label}
          </StatusPill>
        </RowRight>
      </VerificationRow>

      <VerificationRow>
        <RowLeft>
          <RowIcon>
            <Icon icon={faBuildingColumns} label="Funds" />
          </RowIcon>
          <RowText>
            <RowTitle>Funds</RowTitle>
            <RowSubtitle>
              {funds !== null && funds.displayValue !== ''
                ? funds.displayValue
                : 'Show landlords you can cover the rent.'}
            </RowSubtitle>
          </RowText>
        </RowLeft>
        <RowRight>
          <StatusPill tone={fundsDisplay.tone}>
            {funds?.status === 'verified' ? <Icon icon={faCheck} /> : null}
            {fundsDisplay.label}
          </StatusPill>
        </RowRight>
      </VerificationRow>

      <BankActions>
        <PlaidConnectButton
          quickPassUserId={quickPassUser.id}
          isConnected={isBankConnected}
          onStatusMessage={handleStatusMessage}
        />
      </BankActions>

      <GroupTitle>Credit</GroupTitle>

      <VerificationRow>
        <RowLeft>
          <RowIcon>
            <Icon icon={faCreditCard} label="Credit" />
          </RowIcon>
          <RowText>
            <RowTitle>Credit check</RowTitle>
            <RowSubtitle>{creditSubtitle}</RowSubtitle>
          </RowText>
        </RowLeft>
        <RowRight>
          <StatusPill tone={creditDisplay.tone}>
            {creditStatus === 'verified' ? <Icon icon={faCheck} /> : null}
            {creditDisplay.label}
          </StatusPill>
          {isCreditInFlight ? (
            <Button
              type="button"
              variant="secondary"
              size="small"
              isLoading={state.isSyncingCredit}
              onClick={() => {
                void handleSyncCredit()
              }}
            >
              Refresh
            </Button>
          ) : (
            <Button
              type="button"
              variant={creditStatus === 'verified' ? 'secondary' : 'primary'}
              size="small"
              onClick={() => {
                setState(previous => ({
                  ...previous,
                  isCreditModalOpen: true,
                }))
              }}
            >
              {creditStatus === 'verified' ? 'Run again' : 'Run credit check'}
            </Button>
          )}
        </RowRight>
      </VerificationRow>

      {canShowCreditPublicly ? (
        <VisibilityRow>
          <VisibilityText>
            <VisibilityName>Show this on my QuickPass</VisibilityName>
            <VisibilityDescription>
              {isCreditPublic
                ? 'Landlords who open your QuickPass can see that your credit was checked. They never see your report or your score.'
                : 'Your result is private. Turn this on when you are ready for landlords to see it.'}
            </VisibilityDescription>
          </VisibilityText>
          <ToggleSwitch
            isOn={isCreditPublic}
            label="Show my credit check on my QuickPass"
            isDisabled={state.isUpdatingCreditVisibility}
            onToggle={() => {
              void handleToggleCreditVisibility()
            }}
          />
        </VisibilityRow>
      ) : null}

      <CreditCheckModal
        isOpen={state.isCreditModalOpen}
        quickPassUserId={quickPassUser.id}
        onClose={() => {
          setState(previous => ({ ...previous, isCreditModalOpen: false }))
        }}
        onOrdered={message => {
          handleStatusMessage(message, 'info')
        }}
      />

      {state.bannerMessage !== '' ? (
        <Banner tone={state.bannerTone}>{state.bannerMessage}</Banner>
      ) : null}

      <PrivacyNote>
        Contact details are confirmed by QuickPass, identity is powered by
        Didit, income and funds by Plaid, and credit by Certn. We never store
        your ID document, account numbers, or transactions, only the verified
        result you choose to show. Your credit result stays private until you
        turn it on. Read our{' '}
        <PrivacyPolicyLink
          href={QUICKCASA_PRIVACY_POLICY_URL}
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </PrivacyPolicyLink>
        .
      </PrivacyNote>
    </ManagerRoot>
  )
}

export default VerificationsManager
